import { useEffect, useRef, useState } from 'react';
import { bus } from '../../App';
import { usePanelContext } from '../layout/PanelContext';
import { getDashboardAppHostPath } from '../../lib/appSlug'

type LoadState = 'loading' | 'ready' | 'missing';

function isValidUrl(text: string): boolean {
  try {
    const url = new URL(text.trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

interface ConsoleMessage {
  type: 'log' | 'warn' | 'error' | 'info';
  args: any[];
  timestamp: number;
}

export function AppPanel() {
  const { isFocused } = usePanelContext();
  const [appPath] = useState(() => getDashboardAppHostPath())
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const [customUrl, setCustomUrl] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState<string>('');
  const [showConsole, setShowConsole] = useState(false);
  const [consoleMessages, setConsoleMessages] = useState<ConsoleMessage[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const historyStackRef = useRef<string[]>([]);
  const historyIndexRef = useRef<number>(-1);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    async function probeAppRoute() {
      try {
        const response = await fetch(appPath, {
          method: 'HEAD',
          credentials: 'same-origin',
          signal: controller.signal,
        });

        if (!active) return;

        if (response.status === 404) {
          setLoadState('missing');
        } else {
          setLoadState('ready');
        }
      } catch {
        if (!active) return;
        setLoadState('ready');
      }
    }

    probeAppRoute();

    const unsubscribeCheckout = bus.on('files/checkout', () => {
      setReloadKey((prev) => prev + 1);
      probeAppRoute();
    });

    const unsubscribeLlmEnd = bus.on('llm/end', () => {
      setReloadKey((prev) => prev + 1);
      probeAppRoute();
    });

    const unsubscribeReset = bus.on('files/reset', () => {
      setReloadKey((prev) => prev + 1);
      probeAppRoute();
    });

    const unsubscribeDelete = bus.on('file/delete', (data: { path: string }) => {
      if (data.path === 'index.html') {
        setReloadKey((prev) => prev + 1);
        probeAppRoute();
      }
    });
    const unsubscribeHost = bus.on('meta/set', (_event, payload) => {
      if (payload?.key === 'host_version_id') {
        setReloadKey((prev) => prev + 1);
        probeAppRoute();
      }
    });

    return () => {
      active = false;
      controller.abort();
      unsubscribeCheckout();
      unsubscribeReset();
      unsubscribeDelete();
      unsubscribeHost();
      unsubscribeLlmEnd();
    };
  }, []);

  useEffect(() => {
    function handlePaste(event: ClipboardEvent) {
      // Only handle paste if this panel is focused
      if (!isFocused) {
        return;
      }

      const text = event.clipboardData?.getData('text');
      if (text && isValidUrl(text)) {
        event.preventDefault();
        setCustomUrl(text.trim());
        setLoadState('ready');
      }
    }

    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('paste', handlePaste);
    };
  }, [isFocused]);

  const iframeUrl = customUrl || appPath;

  // Track iframe URL changes and console messages
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || loadState !== 'ready') return;

    const trackNavigation = () => {
      try {
        const newUrl = iframe.contentWindow?.location.href;
        if (!newUrl) return;

        // Update displayed URL
        setCurrentUrl(newUrl);

        // Check if this is a new navigation (not back/forward)
        const currentIndex = historyIndexRef.current;
        const lastUrl = historyStackRef.current[currentIndex];

        if (newUrl !== lastUrl) {
          // Remove any forward history
          historyStackRef.current = historyStackRef.current.slice(0, currentIndex + 1);
          // Add new URL
          historyStackRef.current.push(newUrl);
          historyIndexRef.current = historyStackRef.current.length - 1;
          console.log('[AppPanel] Navigation tracked:', newUrl, 'index:', historyIndexRef.current);
        }
      } catch (error) {
        // Cross-origin - can't track
        setCurrentUrl(iframeUrl);
      }
    };

    // Intercept console messages from iframe
    const setupConsoleCapture = () => {
      try {
        const iframeWindow = iframe.contentWindow;
        if (!iframeWindow) return;

        const originalConsole = {
          log: iframeWindow.console.log,
          warn: iframeWindow.console.warn,
          error: iframeWindow.console.error,
          info: iframeWindow.console.info,
        };

        const captureMessage = (type: ConsoleMessage['type']) => {
          return function(...args: any[]) {
            setConsoleMessages(prev => [...prev, {
              type,
              args,
              timestamp: Date.now(),
            }]);
            // Call original console method
            originalConsole[type].apply(iframeWindow.console, args);
          };
        };

        iframeWindow.console.log = captureMessage('log');
        iframeWindow.console.warn = captureMessage('warn');
        iframeWindow.console.error = captureMessage('error');
        iframeWindow.console.info = captureMessage('info');
      } catch (error) {
        // Cross-origin - can't capture console
        console.warn('[AppPanel] Cannot capture iframe console (cross-origin)');
      }
    };

    const onLoad = () => {
      trackNavigation();
      setupConsoleCapture();
    };

    // Track initial load
    iframe.addEventListener('load', onLoad);

    // Poll for URL changes (for SPA navigation and hash changes)
    let lastCheckedUrl = '';
    const pollInterval = setInterval(() => {
      try {
        const currentUrl = iframe.contentWindow?.location.href;
        if (currentUrl && currentUrl !== lastCheckedUrl) {
          lastCheckedUrl = currentUrl;
          trackNavigation();
        }
      } catch (error) {
        // Cross-origin - can't poll
      }
    }, 500);

    return () => {
      iframe.removeEventListener('load', onLoad);
      clearInterval(pollInterval);
    };
  }, [loadState, iframeUrl]);

  const handleBack = () => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current -= 1;
      const url = historyStackRef.current[historyIndexRef.current];
      console.log('[AppPanel] Going back to:', url, 'index:', historyIndexRef.current);
      if (iframeRef.current) {
        iframeRef.current.src = url;
      }
    }
  };

  const handleForward = () => {
    if (historyIndexRef.current < historyStackRef.current.length - 1) {
      historyIndexRef.current += 1;
      const url = historyStackRef.current[historyIndexRef.current];
      console.log('[AppPanel] Going forward to:', url, 'index:', historyIndexRef.current);
      if (iframeRef.current) {
        iframeRef.current.src = url;
      }
    }
  };

  const handleReload = () => {
    setReloadKey((prev) => prev + 1);
  };

  const handleClearConsole = () => {
    setConsoleMessages([]);
  };

  const formatConsoleArg = (arg: any): string => {
    if (typeof arg === 'object' && arg !== null) {
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    }
    return String(arg);
  };

  const canGoBack = historyIndexRef.current > 0;
  const canGoForward = historyIndexRef.current < historyStackRef.current.length - 1;

  return (
    <div ref={panelRef} className="panel-shell">
      <div className="panel-controls">
        <button
          className="btn-ghost"
          onClick={handleBack}
          disabled={!canGoBack || loadState !== 'ready'}
          title="Back"
        >
          ←
        </button>
        <button
          className="btn-ghost"
          onClick={handleReload}
          disabled={loadState !== 'ready'}
          title="Reload"
        >
          ↻
        </button>
        <button
          className="btn-ghost"
          onClick={handleForward}
          disabled={!canGoForward || loadState !== 'ready'}
          title="Forward"
        >
          →
        </button>
        <button
          className="btn-ghost"
          onClick={() => window.open(currentUrl || iframeUrl, '_blank', 'noopener,noreferrer')}
          disabled={loadState !== 'ready'}
          title="Open in new browser tab"
        >
          Browser
        </button>
      </div>
      <div className="app-panel-content">
        {loadState === 'ready' ? (
          <iframe
            ref={iframeRef}
            key={reloadKey}
            src={iframeUrl}
            className="app-panel-iframe"
            title="Application"
          />
        ) : (
          <div className="app-panel-empty">
            {loadState === 'loading' ? (
              <span className="app-panel-loading">
                Loading preview...
              </span>
            ) : (
              <span className="app-panel-error">
                404
              </span>
            )}
          </div>
        )}
      </div>
      {showConsole && (
        <div className="app-panel-console">
          <div className="app-panel-console-header">
            <span className="app-panel-console-title">Console</span>
            <button className="btn-ghost" onClick={handleClearConsole} title="Clear Console">
              Clear
            </button>
          </div>
          <div className="app-panel-console-messages">
            {consoleMessages.length === 0 ? (
              <div className="app-panel-console-empty">No console messages</div>
            ) : (
              consoleMessages.map((msg, index) => (
                <div key={index} className={`app-panel-console-message app-panel-console-message--${msg.type}`}>
                  <span className="app-panel-console-timestamp">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </span>
                  <span className="app-panel-console-content">
                    {msg.args.map(formatConsoleArg).join(' ')}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>
      )}
      {currentUrl && (
        <div className="app-panel-url-bar">
          <span className="app-panel-url-text">{currentUrl}</span>
          <button
            className="btn-ghost"
            onClick={() => setShowConsole(!showConsole)}
            title={showConsole ? 'Hide Console' : 'Show Console'}
          >
            Console {consoleMessages.length > 0 && `(${consoleMessages.length})`}
          </button>
        </div>
      )}
    </div>
  );
}
